/**
 * "Is this reply still the one anybody is waiting for?", for store code.
 *
 * `useLatestRequest` answers it for screens, but a store cannot call a hook.
 * `sessionStore` had written the answer by hand five times, as a module counter
 * compared after each `await`, with `!==` beside `===` on the same counter in
 * one `finally`. The bookkeeping lives here once, and the call site picks a
 * verb:
 *
 * - **`begin`** supersedes every earlier request and returns this one's test.
 *   For a request that replaces the last: a search, a cancel.
 * - **`retire`** supersedes every earlier request and starts nothing. For a
 *   reset, or standing a search down.
 * - **`observe`** returns a test without superseding anything. For requests
 *   that run side by side, which only a reset or a newer `begin` cancels.
 *
 * As in `useLatestRequest`, the counter is never exposed, so no call site can
 * compare it wrongly. `.claude/guidance/ts/async_lifecycles.md` § "Recheck
 * ownership before acting after a wait" is the rule this implements.
 */
export interface Generation {
  /** Claims ownership for the work starting now; returns "still mine?". */
  readonly begin: () => () => boolean;
  /** Retires whatever is in flight; nothing new starts. */
  readonly retire: () => void;
  /** "Still mine?" for work that shares the current generation. */
  readonly observe: () => () => boolean;
}

export function generation(): Generation {
  let count = 0;
  const test = (mine: number) => (): boolean => mine === count;
  return {
    begin: () => {
      count += 1;
      return test(count);
    },
    retire: () => {
      count += 1;
    },
    observe: () => test(count),
  };
}
